import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Chip as MuiChip,
  Divider,
  Link,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import { ThemeProvider, useTheme } from '@mui/material/styles';

import LelyMuiTheme, {
  lelyTokenPrimitives,
} from '../../common/styles/newStyles/LelyMuiTheme';
import LelyChip from '@/common/components/muiWrappers/LelyChip';
import ToggleTip from '@/common/components/ToggleTip';
import Icon from '@/common/components/IconSvg/Icon';
import { LelyButton, LelyTextField } from '@/common/components';

const lelyPrimitives = lelyTokenPrimitives as Record<string, string>;

const SCALE_STEPS = [
  '10',
  '20',
  '30',
  '40',
  '50',
  '60',
  '70',
  '80',
  '90',
  '99',
] as const;

const scaleSwatches = (familyPrefix: string) =>
  SCALE_STEPS.map((step) => ({
    label: step,
    color: lelyPrimitives[`${familyPrefix}${step}`],
  }));

const LELY_COLOR_SCALE_FAMILIES = [
  { title: 'Neutral', prefix: 'colorNeutral' },
  { title: 'Magenta', prefix: 'colorMagenta' },
  { title: 'Purple', prefix: 'colorPurple' },
  { title: 'Cyan', prefix: 'colorCyan' },
  { title: 'Teal', prefix: 'colorTeal' },
  { title: 'Green', prefix: 'colorGreen' },
  { title: 'Yellow', prefix: 'colorYellow' },
  { title: 'Orange', prefix: 'colorOrange' },
  { title: 'Red', prefix: 'colorRed' },
  { title: 'Error', prefix: 'colorError' },
  { title: 'Warning', prefix: 'colorWarning' },
  { title: 'Advice', prefix: 'colorAdvice' },
  { title: 'Success', prefix: 'colorSuccess' },
  { title: 'A11y red', prefix: 'colorA11yRed' },
] as const;

const LELY_BASE_COLORS = [
  {
    title: 'White',
    steps: [{ label: '100', color: lelyPrimitives.colorWhite100 }],
  },
  {
    title: 'Black',
    steps: [{ label: '100', color: lelyPrimitives.colorBlack100 }],
  },
] as const;

/** Styleguide names ↔ `lelyTokenPrimitives` keys (from @lely/tokens spacing map). */
const LELY_SPACING_STYLEGUIDE = [
  { name: '2xs', primitiveKey: 'space2xs' },
  { name: 'xs', primitiveKey: 'spaceXs' },
  { name: 's', primitiveKey: 'spaceSm' },
  { name: 'm', primitiveKey: 'spaceMd' },
  { name: 'l', primitiveKey: 'spaceLg' },
  { name: 'xl', primitiveKey: 'spaceXl' },
  { name: '2xl', primitiveKey: 'space2xl' },
  { name: '3xl', primitiveKey: 'space3xl' },
  { name: '4xl', primitiveKey: 'space4xl' },
  { name: '5xl', primitiveKey: 'space5xl' },
  { name: '6xl', primitiveKey: 'space6xl' },
  { name: '7xl', primitiveKey: 'space7xl' },
  { name: '8xl', primitiveKey: 'space8xl' },
  { name: '9xl', primitiveKey: 'space9xl' },
  { name: '10xl', primitiveKey: 'space10xl' },
  { name: '11xl', primitiveKey: 'space11xl' },
  { name: '12xl', primitiveKey: 'space12xl' },
  { name: '13xl', primitiveKey: 'space13xl' },
  { name: '14xl', primitiveKey: 'space14xl' },
  { name: '15xl', primitiveKey: 'space15xl' },
  { name: '16xl', primitiveKey: 'space16xl' },
] as const;

const LELY_BORDER_WIDTH = [
  { styleguide: 'xl', primitiveKey: 'borderWidthXl' },
  { styleguide: 'lg', primitiveKey: 'borderWidthLg' },
  { styleguide: 'base', primitiveKey: 'borderWidthBase' },
] as const;

const LELY_BORDER_RADIUS = [
  { styleguide: '4xl', primitiveKey: 'borderRadius4xl' },
  { styleguide: '3xl', primitiveKey: 'borderRadius3xl' },
  { styleguide: '2xl', primitiveKey: 'borderRadius2xl' },
  { styleguide: 'xl', primitiveKey: 'borderRadiusXl' },
  { styleguide: 'lg', primitiveKey: 'borderRadiusLg' },
  { styleguide: 'md', primitiveKey: 'borderRadiusMd' },
  { styleguide: 'sm', primitiveKey: 'borderRadiusSm' },
] as const;

const LELY_BORDER_STYLE_ROWS = [
  {
    styleguide: 'Normal',
    value: 'Solid',
    primitiveKey: 'borderNormal',
    note: 'Default UI border (1px solid)',
  },
  {
    styleguide: 'Dashed',
    value: 'Dashed',
    primitiveKey: 'borderDashed',
    note: '1px dashed',
  },
  {
    styleguide: 'Focus',
    value: 'Solid',
    primitiveKey: 'borderFocus',
    note: 'Focus ring (2px solid) — from tokens',
  },
] as const;

const LELY_SHADOW_SCALE = [
  { styleguide: 'sm', primitiveKey: 'boxShadowSm' },
  { styleguide: 'md', primitiveKey: 'boxShadowMd' },
  { styleguide: 'lg', primitiveKey: 'boxShadowLg' },
  { styleguide: 'xl', primitiveKey: 'boxShadowXl' },
  { styleguide: '2xl', primitiveKey: 'boxShadow2xl' },
  { styleguide: '3xl', primitiveKey: 'boxShadow3xl' },
] as const;

function ColorSwatchRow({
  steps,
}: {
  steps: readonly { label: string; color: string }[];
}) {
  return (
    <Stack direction="row" flexWrap="wrap" gap={0.5}>
      {steps.map(({ label, color }) => (
        <Stack key={label} alignItems="center" spacing={0.25}>
          <Box
            sx={{
              width: 40,
              height: 40,
              bgcolor: color,
              borderRadius: 1,
              border: 1,
              borderColor: 'divider',
            }}
          />
          <Typography variant="caption" sx={{ fontSize: 10 }}>
            {label}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
}

const LELY_TEXT_FIELD_VARIANTS = [
  { title: 'Default', props: { label: 'Label' } },
  {
    title: 'Placeholder',
    props: { label: 'Label', placeholder: 'Placeholder' },
  },
  { title: 'With value', props: { label: 'Label', defaultValue: 'Value' } },
  {
    title: 'Helper text',
    props: { label: 'Label', helperText: 'Optional helper text' },
  },
  {
    title: 'Unit',
    props: { label: 'Label', unitText: 'kg', defaultValue: '12' },
  },
  {
    title: 'Icon',
    props: { label: 'Label', icon: 'lely-icon-information' as const },
  },
  {
    title: 'Unit + icon',
    props: {
      label: 'Label',
      unitText: 'kg',
      icon: 'lely-icon-information' as const,
      defaultValue: '12',
    },
  },
  {
    title: 'Helper + icon',
    props: {
      label: 'Label',
      helperText: 'Optional helper text',
      icon: 'lely-icon-information' as const,
    },
  },
  {
    title: 'Helper + unit + icon',
    props: {
      label: 'Label',
      helperText: 'Optional helper text',
      unitText: 'kg',
      icon: 'lely-icon-information' as const,
      defaultValue: '12',
    },
  },
  {
    title: 'Error',
    props: {
      label: 'Label',
      helperText: 'This field is required',
      valid: false,
    },
  },
  {
    title: 'Error + errorMessage',
    props: { label: 'Label', errorMessage: 'This field is required' },
  },
  {
    title: 'Error + unit + icon',
    props: {
      label: 'Label',
      helperText: 'Enter a valid number',
      valid: false,
      unitText: 'kg',
      icon: 'lely-icon-information' as const,
      defaultValue: '12',
    },
  },
  { title: 'Disabled', props: { label: 'Label', disabled: true } },
  {
    title: 'Disabled with value',
    props: { label: 'Label', defaultValue: 'Value', disabled: true },
  },
  {
    title: 'Disabled + helper',
    props: {
      label: 'Label',
      helperText: 'Optional helper text',
      disabled: true,
    },
  },
  {
    title: 'Hidden label',
    props: { hiddenLabel: true, placeholder: 'Search...' },
  },
  { title: 'Required', props: { label: 'Label', required: true } },
] as const;

function ThemePlaygroundContent() {
  const [tab, setTab] = useState(0);
  const theme = useTheme();

  return (
    <Box
      sx={{
        height: '100vh',
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
        bgcolor: 'background.default',
        color: 'text.primary',
        py: 3,
        px: { xs: 2, sm: 4 },
        maxWidth: 960,
        mx: 'auto',
      }}
    >
      <Stack spacing={1} sx={{ mb: 3 }}>
        <Typography variant="h5" component="h1">
          Lely MUI theme playground
        </Typography>
        <Typography variant="body2" color="text.secondary">
          This route is wrapped in its own{' '}
          <Box component="code" sx={{ fontFamily: 'monospace' }}>
            ThemeProvider
          </Box>{' '}
          using{' '}
          <Box component="code" sx={{ fontFamily: 'monospace' }}>
            LelyMuiTheme
          </Box>{' '}
          (values from{' '}
          <Box component="code" sx={{ fontFamily: 'monospace' }}>
            @lely/tokens
          </Box>
          ).
        </Typography>
        <Link component={RouterLink} to="/" variant="body2" color="primary">
          ← Back to home
        </Link>
      </Stack>

      <Stack spacing={4}>
        <section>
          <Typography variant="subtitle1" gutterBottom fontWeight="medium">
            Palette (theme.palette)
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={1}>
            <MuiChip label="primary" color="primary" />
            <MuiChip label="secondary" color="secondary" />
            <MuiChip label="error" color="error" />
            <MuiChip label="warning" color="warning" />
            <MuiChip label="success" color="success" />
            <MuiChip label="info" color="info" />
          </Stack>
        </section>

        <section>
          <Typography variant="subtitle1" gutterBottom fontWeight="medium">
            Neutral (Lely styleguide scale)
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Use MUI semantic paths when they match your intent (these map to
            neutrals in <code>LelyMuiTheme</code>). Use{' '}
            <code>lelyTokenPrimitives</code> when you need an exact step like
            neutral.40.
          </Typography>
          <Stack spacing={2}>
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                1. <code>sx</code> / <code>color</code> props — palette keys
              </Typography>
              <Typography sx={{ color: 'text.primary' }}>
                text.primary → neutral.10
              </Typography>
              <Typography sx={{ color: 'text.secondary' }}>
                text.secondary → neutral.50
              </Typography>
              <Typography sx={{ color: 'text.disabled' }}>
                text.disabled → neutral.60
              </Typography>
              <Box
                sx={{
                  mt: 1,
                  height: 4,
                  bgcolor: 'divider',
                  borderRadius: 1,
                }}
              />
              <Typography variant="caption" color="text.secondary">
                divider → neutral.80
              </Typography>
            </Box>
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                2. <code>useTheme()</code> — same values, in JS
              </Typography>
              <Typography variant="body2">
                theme.palette.text.primary = {theme.palette.text.primary}
              </Typography>
              <Typography variant="body2">
                theme.palette.background.default ={' '}
                {theme.palette.background.default}
              </Typography>
            </Box>
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                3. <code>lelyTokenPrimitives</code> — raw @lely/tokens neutrals
              </Typography>
              <ColorSwatchRow steps={scaleSwatches('colorNeutral')} />
            </Box>
          </Stack>
        </section>

        <section>
          <Typography variant="subtitle1" gutterBottom fontWeight="medium">
            All Lely color primitives (lelyTokenPrimitives)
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Full scales from <code>@lely/tokens/dist/colors.scss</code>,
            exported via <code>lelyTokenPrimitives.module.scss</code>.
          </Typography>
          <Stack spacing={2}>
            {LELY_BASE_COLORS.map(({ title, steps }) => (
              <Box key={title}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  {title}
                </Typography>
                <ColorSwatchRow steps={steps} />
              </Box>
            ))}
            {LELY_COLOR_SCALE_FAMILIES.map(({ title, prefix }) => (
              <Box key={prefix}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  {title}
                </Typography>
                <ColorSwatchRow steps={scaleSwatches(prefix)} />
              </Box>
            ))}
          </Stack>
        </section>

        <section>
          <Typography variant="subtitle1" gutterBottom fontWeight="medium">
            Typography (Ubuntu headings, Noto body — from tokens)
          </Typography>
          <Stack spacing={0.5}>
            <Typography variant="h1">Heading h1 (text-6xl)</Typography>

            <Typography variant="h6">Heading h6 (text-xl)</Typography>
            <Typography variant="subtitle1">Subtitle1 (text-lg)</Typography>
            <Typography variant="body1">
              Body1 — base 16 / 24 from Lely text scale.
            </Typography>
            <Typography variant="body2">
              Body2 — md 14 / 20; secondary tone is palette text.secondary.
            </Typography>
            <Typography variant="caption" display="block">
              Caption — xs 12 / 16, subtle colour.
            </Typography>
          </Stack>
        </section>

        <section>
          <Typography variant="subtitle1" gutterBottom fontWeight="medium">
            Spacing (Lely styleguide — @lely/tokens)
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Spacing keeps layouts consistent. The 8px base unit underpins the
            token system; the default step is <strong>s</strong> (8px). Use
            tokens instead of arbitrary pixel values. In Figma, variables follow{' '}
            <code>spacing-(sm, md, base, lg, …)</code> naming.
          </Typography>
          <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
            Small spaces (about 0–12px)
          </Typography>
          <Box
            component="ul"
            sx={{
              pl: 2,
              mb: 2,
              m: 0,
              color: 'text.secondary',
              typography: 'body2',
            }}
          >
            <li>
              Padding in inputs, gaps between text and icons, tight button
              groups
            </li>
            <li>Small component padding (icon buttons, badges, tooltips)</li>
            <li>Table cells and dense report layouts</li>
          </Box>
          <Typography variant="subtitle2" gutterBottom>
            Medium spaces (about 12–24px)
          </Typography>
          <Box
            component="ul"
            sx={{
              pl: 2,
              mb: 2,
              m: 0,
              color: 'text.secondary',
              typography: 'body2',
            }}
          >
            <li>Vertical rhythm inside cards (title, description, actions)</li>
            <li>Padding for larger controls; graph vertical spacing</li>
          </Box>
          <Typography variant="subtitle2" gutterBottom>
            Big spaces (about 26–80px)
          </Typography>
          <Box
            component="ul"
            sx={{
              pl: 2,
              mb: 2,
              m: 0,
              color: 'text.secondary',
              typography: 'body2',
            }}
          >
            <li>Page-level gaps (e.g. space from top of page to header)</li>
          </Box>
          <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
            Token table
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <strong>Spacing</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Value</strong>
                  </TableCell>
                  <TableCell>
                    <strong>lelyTokenPrimitives</strong>
                  </TableCell>
                  <TableCell width="40%">
                    <strong>Preview</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {LELY_SPACING_STYLEGUIDE.map(({ name, primitiveKey }) => {
                  const value = lelyPrimitives[primitiveKey];
                  return (
                    <TableRow key={primitiveKey}>
                      <TableCell>{name}</TableCell>
                      <TableCell>{value}</TableCell>
                      <TableCell>
                        <Box component="code" sx={{ fontSize: '0.75rem' }}>
                          {primitiveKey}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            height: 20,
                            width: value,
                            maxWidth: '100%',
                            bgcolor: 'primary.main',
                            borderRadius: 0.5,
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="subtitle2" gutterBottom>
            MUI <code>theme.spacing</code> (4px per unit)
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            <code>LelyMuiTheme</code> uses MUI&apos;s default grid:{' '}
            <code>spacing(1) = 4px</code>, <code>spacing(2) = 8px</code>, etc.
            For an exact Lely step (e.g. <code>m</code> = 12px), use{' '}
            <code>lelyTokenPrimitives.spaceMd</code> in <code>sx</code> or
            styles.
          </Typography>
          <Stack direction="row" alignItems="flex-end" gap={1} flexWrap="wrap">
            {[1, 2, 3, 4, 6].map((n) => (
              <Stack key={n} alignItems="center" spacing={0.25}>
                <Box
                  sx={{
                    width: (t) => t.spacing(n),
                    height: (t) => t.spacing(n),
                    bgcolor: 'secondary.main',
                    borderRadius: 1,
                  }}
                  title={`theme.spacing(${n})`}
                />
                <Typography variant="caption" sx={{ fontSize: 10 }}>
                  ({n})
                </Typography>
              </Stack>
            ))}
          </Stack>
        </section>

        <section>
          <Typography variant="subtitle1" gutterBottom fontWeight="medium">
            Borders (Lely styleguide — @lely/tokens)
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Use borders to add contrast. Default <strong>base</strong> width
            (1px) suits buttons and dividers. The styleguide default corner
            radius for buttons, fields, and cards is <strong>sm</strong> (4px).
          </Typography>

          <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
            Border width
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            <code>border-width</code> sets the thickness of all four sides. One
            to four values follow CSS shorthand rules.
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <strong>Border width</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Value</strong>
                  </TableCell>
                  <TableCell>
                    <strong>lelyTokenPrimitives</strong>
                  </TableCell>
                  <TableCell width="36%">
                    <strong>Preview</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {LELY_BORDER_WIDTH.map(({ styleguide, primitiveKey }) => {
                  const w = lelyPrimitives[primitiveKey];
                  return (
                    <TableRow key={primitiveKey}>
                      <TableCell>{styleguide}</TableCell>
                      <TableCell>{w}</TableCell>
                      <TableCell>
                        <Box component="code" sx={{ fontSize: '0.75rem' }}>
                          {primitiveKey}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            width: 100,
                            height: 0,
                            borderBottomWidth: w,
                            borderBottomStyle: 'solid',
                            borderColor: 'divider',
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="subtitle2" gutterBottom>
            Border radius
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            <code>border-radius</code> rounds corners. <code>LelyMuiTheme</code>{' '}
            sets <code>theme.shape.borderRadius</code> to the{' '}
            <strong>md</strong> token (6px); use{' '}
            <code>lelyTokenPrimitives.borderRadiusSm</code> when you need the
            styleguide <strong>sm</strong> default (4px).
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <strong>Border radius</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Value</strong>
                  </TableCell>
                  <TableCell>
                    <strong>lelyTokenPrimitives</strong>
                  </TableCell>
                  <TableCell width="36%">
                    <strong>Preview</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {LELY_BORDER_RADIUS.map(({ styleguide, primitiveKey }) => {
                  const r = lelyPrimitives[primitiveKey];
                  return (
                    <TableRow key={primitiveKey}>
                      <TableCell>{styleguide}</TableCell>
                      <TableCell>{r}</TableCell>
                      <TableCell>
                        <Box component="code" sx={{ fontSize: '0.75rem' }}>
                          {primitiveKey}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            width: 48,
                            height: 48,
                            bgcolor: 'action.hover',
                            border: 1,
                            borderColor: 'divider',
                            borderRadius: r,
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="subtitle2" gutterBottom>
            Border style
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            <code>border-style</code> sets line style. Presets combine width,
            style, and colour in <code>@lely/tokens/dist/borders.scss</code>.
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <strong>Border style</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Value</strong>
                  </TableCell>
                  <TableCell>
                    <strong>lelyTokenPrimitives</strong>
                  </TableCell>
                  <TableCell width="36%">
                    <strong>Preview</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {LELY_BORDER_STYLE_ROWS.map(
                  ({ styleguide, value, primitiveKey, note }) => {
                    const border = lelyPrimitives[primitiveKey];
                    return (
                      <TableRow key={primitiveKey}>
                        <TableCell>{styleguide}</TableCell>
                        <TableCell>{value}</TableCell>
                        <TableCell>
                          <Box component="code" sx={{ fontSize: '0.75rem' }}>
                            {primitiveKey}
                          </Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                          >
                            {note}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              width: 72,
                              height: 40,
                              border,
                              borderRadius: 1,
                              bgcolor: 'background.paper',
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  },
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </section>

        <section>
          <Typography variant="subtitle1" gutterBottom fontWeight="medium">
            Shadows (Lely styleguide — @lely/tokens)
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Shadows add depth and draw attention. Prefer them sparingly—for
            dialogs and elements that must read above the rest of the UI.
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Each token stacks multiple layers (X/Y offset, blur, spread,
            colour). Tokens compile to <code>box-shadow</code> using neutral.70
            (<code>#ACA7A7</code>) with opacity in the alpha channel—see the
            styleguide for per-layer specs.
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <strong>Shadow</strong>
                  </TableCell>
                  <TableCell>
                    <strong>lelyTokenPrimitives</strong>
                  </TableCell>
                  <TableCell sx={{ minWidth: 200 }}>
                    <strong>Value (CSS)</strong>
                  </TableCell>
                  <TableCell width={160}>
                    <strong>Preview</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {LELY_SHADOW_SCALE.map(({ styleguide, primitiveKey }) => {
                  const shadow = lelyPrimitives[primitiveKey];
                  return (
                    <TableRow key={primitiveKey}>
                      <TableCell>{styleguide}</TableCell>
                      <TableCell>
                        <Box component="code" sx={{ fontSize: '0.75rem' }}>
                          {primitiveKey}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="caption"
                          component="code"
                          sx={{
                            display: 'block',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-all',
                            fontSize: '0.7rem',
                          }}
                        >
                          {shadow}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            width: 120,
                            height: 72,
                            mx: 'auto',
                            bgcolor: 'background.paper',
                            borderRadius: 1,
                            border: 1,
                            borderColor: 'divider',
                            boxShadow: shadow,
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="body2" color="text.secondary">
            In <code>sx</code>:{' '}
            <code>boxShadow: lelyTokenPrimitives.boxShadowMd</code> or{' '}
            <code>theme.shadows[n]</code> — see elevation section for MUI{' '}
            <code>Paper</code> mapping.
          </Typography>
        </section>

        <section>
          <Typography variant="subtitle1" gutterBottom fontWeight="medium">
            Elevation (MUI Paper — Lely shadows)
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            <code>LelyMuiTheme</code> maps <code>theme.shadows[1–6]</code> to
            Lely tokens: <strong>sm</strong> → <strong>3xl</strong>. Use{' '}
            <code>{'<Paper elevation={n} />'}</code> with <code>n</code> from
            1–6, or assign <code>boxShadow</code> from{' '}
            <code>lelyTokenPrimitives</code> directly.
          </Typography>
          <Stack
            direction="row"
            flexWrap="wrap"
            gap={2}
            justifyContent="flex-start"
          >
            {(
              [
                [1, 'sm'],
                [2, 'md'],
                [3, 'lg'],
                [4, 'xl'],
                [5, '2xl'],
                [6, '3xl'],
              ] as const
            ).map(([e, token]) => (
              <Paper key={e} elevation={e} sx={{ p: 2, width: 140 }}>
                <Typography variant="body2" fontWeight="medium">
                  elevation={e}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {token}
                </Typography>
              </Paper>
            ))}
          </Stack>
        </section>

        <section>
          <Typography variant="subtitle1" gutterBottom fontWeight="medium">
            Components (styleOverrides from LelyMuiTheme)
          </Typography>
          <Stack spacing={2} alignItems="flex-start">
            <Stack direction="row" flexWrap="wrap" gap={1}>
              <LelyButton variant="primary">
                Primary
              </LelyButton>
              <LelyButton variant="secondary">
                Secondary
              </LelyButton>
              <LelyButton variant="tertiary">
                Tertiary
              </LelyButton>
            </Stack>
            <Typography variant="subtitle2" fontWeight="medium">
              LelyTextField
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Filled field with optional helper text, unit, and trailing icon.
              Hover, focus, active, and error borders come from{' '}
              <code>MuiFilledInput</code> in <code>LelyMuiTheme</code>.
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 2,
                width: '100%',
              }}
            >
              {LELY_TEXT_FIELD_VARIANTS.map(({ title, props }) => (
                <Stack key={title} spacing={0.5} sx={{ width: '100%' }}>
                  <Typography variant="caption" color="text.secondary">
                    {title}
                  </Typography>
                  <LelyTextField {...props} />
                </Stack>
              ))}
            </Box>
            <Tabs value={tab} onChange={(_, v) => setTab(v)}>
              <Tab label="First" />
              <Tab label="Second" />
            </Tabs>
            <Typography variant="subtitle2" fontWeight="medium">
              Tooltip — hover (MUI <code>Tooltip</code>)
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Use for icons, labels, and controls. Styled via{' '}
              <code>lelyTooltipSurfaceStyles</code> in <code>LelyMuiTheme</code>
              : <code>title</code>, <code>placement</code>, <code>arrow</code>.
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={2} alignItems="center">
              <Tooltip
                title="Behold, a tooltip!"
                placement="top"
                arrow
                data-testid="theme-playground-tooltip-1"
              >
                <LelyButton variant="primary" color="primary">
                  With arrow (top)
                </LelyButton>
              </Tooltip>
              <Tooltip
                title="No arrow variant"
                placement="bottom"
                arrow={false}
                data-testid="theme-playground-tooltip-2"
              >
                <LelyButton variant="secondary">
                  Without arrow (bottom)
                </LelyButton>
              </Tooltip>
            </Stack>
            <Typography variant="subtitle2" fontWeight="medium" sx={{ mt: 2 }}>
              PositionedTooltip — charts &amp; timelines
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Use <code>PositionedTooltip</code> when placement is computed (
              <code>left</code>/<code>top</code>/<code>bottom</code> +{' '}
              <code>--arrow-offset</code>). Same surface styles as MUI Tooltip;
              not a hover wrapper.
            </Typography>
            <Typography variant="subtitle2" fontWeight="medium" sx={{ mt: 2 }}>
              Chip — dismissible &amp; selectable (Lely)
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <code>variant=&quot;dismissible&quot;</code> with{' '}
              <code>onDelete</code> for removable filters.{' '}
              <code>variant=&quot;selectable&quot;</code> is structured for later
              interaction. Use <code>dense</code> only on non-touch layouts;{' '}
              <code>inverted</code> on brand-primary backgrounds.
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={1}>
              <LelyChip
                label="Device: Robot"
                variant="dismissible"
                onDelete={() => undefined}
                data-testid="theme-playground-dismissible-chip"
              />
              <LelyChip
                label="Keyword"
                variant="dismissible"
                onDelete={() => undefined}
              />
              <LelyChip label="Chips" variant="selectable" />
              <LelyChip label="Chips" variant="selectable" dense />
            </Stack>
            <Box
              sx={{
                mt: 1,
                p: 2,
                bgcolor: 'primary.main',
                borderRadius: 1,
              }}
            >
              <Stack direction="row" flexWrap="wrap" gap={1}>
                <LelyChip
                  label="Chips"
                  variant="dismissible"
                  inverted
                  onDelete={() => undefined}
                />
                <LelyChip label="Chips" variant="selectable" inverted />
              </Stack>
            </Box>
            <Typography variant="subtitle2" fontWeight="medium" sx={{ mt: 2 }}>
              ToggleTip — click info icon
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Lely signature: <code>position</code>, <code>headerText</code>,{' '}
              <code>bodyText</code>, <code>chevron</code>,{' '}
              <code>containsLink</code>, optional <code>link</code> /{' '}
              <code>linkText</code>. Closes via close button or click-away.
            </Typography>
            <ToggleTip
              headerText="This is a title"
              body="This is a toggle tip. It has text in the body."
              position="top"
              chevron
              containsLink
              link="#"
              linkText="Link"
            >
              <Box display="inline-flex" alignItems="center">
                <Icon name="lely-icon-information" />
              </Box>
            </ToggleTip>
          </Stack>
        </section>

        <section>
          <Typography variant="subtitle1" gutterBottom fontWeight="medium">
            CSS variables (theme.default.scss — usable outside MUI)
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.paper' }}>
            <Typography
              variant="body2"
              sx={{ color: 'var(--theme-text-brand-primary)' }}
            >
              --theme-text-brand-primary
            </Typography>
            <Divider sx={{ my: 1 }} />
            <Typography
              variant="body2"
              sx={{
                bgcolor: 'var(--theme-background-success-subtle)',
                p: 1,
                borderRadius: 1,
              }}
            >
              Background from --theme-background-success-subtle
            </Typography>
          </Paper>
        </section>
      </Stack>
    </Box>
  );
}

/** Dev route: always uses `LelyMuiTheme` even if the app root theme changes. */
export default function ThemePlaygroundPage() {
  return (
    <ThemeProvider theme={LelyMuiTheme}>
      <ThemePlaygroundContent />
    </ThemeProvider>
  );
}
